import React, { useState, useEffect, useCallback } from 'react';
import { Tag, TagUsage } from '../../types';
import { tagService } from '../../services';
import { CreateTagRequest } from '../../services/tagService';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Card, { CardBody } from '../../components/ui/Card';
import EmptyState from '../../components/ui/EmptyState';
import SearchBar from '../../components/ui/SearchBar';
import TagBadge from '../../components/common/TagBadge';

// =============================================================================
// Reusable section header (kept here so this file is self-contained)
// =============================================================================

const SectionHeader: React.FC<{ title: string; description: string }> = ({ title, description }) => (
  <div className="mb-6">
    <h2 className="text-2xl font-semibold text-neutral-900">{title}</h2>
    <p className="text-sm text-neutral-500 mt-1">{description}</p>
  </div>
);

// =============================================================================
// Count cell — clickable when > 0, dim when 0
// =============================================================================

const CountCell: React.FC<{ value: number; onClick: () => void }> = ({ value, onClick }) => {
  if (value === 0) {
    return <span className="text-neutral-400">—</span>;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-primary-600 hover:text-primary-700 font-medium"
    >
      {value}
    </button>
  );
};

// =============================================================================
// Tag form modal (create + edit)
// =============================================================================

interface TagFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateTagRequest, id?: string) => Promise<void>;
  editing: Tag | null;
}

const PRESET_COLORS = [
  '#64748b', // slate
  '#3b82f6', // blue
  '#22c55e', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#a855f7', // purple
  '#14b8a6', // teal
  '#ec4899', // pink
];

const TagFormModal: React.FC<TagFormModalProps> = ({ isOpen, onClose, onSubmit, editing }) => {
  const [name, setName] = useState('');
  const [color, setColor] = useState<string | undefined>(undefined);
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    if (editing) {
      setName(editing.name);
      setColor(editing.color || undefined);
      setDescription(editing.description ?? '');
    } else {
      setName('');
      setColor(undefined);
      setDescription('');
    }
    setError(null);
  }, [isOpen, editing]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    try {
      setSubmitting(true);
      await onSubmit(
        {
          name: name.trim(),
          color,
          description: description.trim() || undefined,
        },
        editing?.id,
      );
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to save tag.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/70 animate-fade-in" onClick={onClose} />
      <div className="relative bg-surface rounded-xl shadow-2xl border border-neutral-200 max-w-md w-full mx-auto z-10 animate-scale-in">
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-neutral-900">
            {editing ? 'Edit Tag' : 'New Tag'}
          </h3>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-600 transition-colors"
            aria-label="Close modal"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {error && (
            <div className="p-3 bg-danger-50 border border-danger-200 rounded-lg text-sm text-danger-700">
              {error}
            </div>
          )}

          <Input
            label="Name"
            required
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Earnings"
          />

          <div>
            <label className="block text-xs font-medium text-neutral-500 mb-2">Color</label>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setColor(undefined)}
                className={`w-7 h-7 rounded-full border-2 flex items-center justify-center text-neutral-400 ${
                  color === undefined ? 'border-primary-500' : 'border-neutral-300'
                }`}
                title="No color"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              {PRESET_COLORS.map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`w-7 h-7 rounded-full border-2 ${
                    color === c ? 'border-neutral-900' : 'border-neutral-300'
                  }`}
                  style={{ backgroundColor: c }}
                  title={c}
                />
              ))}
            </div>
          </div>

          <Input
            label="Description"
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Optional"
          />

          {/* Live preview */}
          {name.trim() && (
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-2">Preview</label>
              <TagBadge
                tag={{
                  id: 'preview',
                  ownerId: '',
                  name: name.trim(),
                  color,
                  description,
                  eventCount: 0,
                  workflowCount: 0,
                  dashboardCount: 0,
                  usageCount: 0,
                  createdAt: '',
                  updatedAt: '',
                }}
              />
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" type="button" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" type="submit" disabled={submitting}>
              {submitting ? 'Saving…' : editing ? 'Save Changes' : 'Create Tag'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// =============================================================================
// Usage drawer — shown when the user clicks "Where used"
// =============================================================================

interface UsageDrawerProps {
  tag: Tag;
  onClose: () => void;
}

const UsageDrawer: React.FC<UsageDrawerProps> = ({ tag, onClose }) => {
  const [usage, setUsage] = useState<TagUsage[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    tagService
      .usage(tag.id)
      .then(data => {
        if (!cancelled) setUsage(data);
      })
      .catch(() => {
        if (!cancelled) setUsage([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tag.id]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative bg-surface rounded-xl shadow-2xl border border-neutral-200 max-w-lg w-full mx-auto z-10">
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm text-neutral-500">Where used:</span>
            <TagBadge tag={tag} />
          </div>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-600 transition-colors"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="px-6 py-5 max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="flex items-center gap-2 text-neutral-500">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-neutral-300 border-t-primary-600" />
              <span className="text-sm">Loading…</span>
            </div>
          ) : !usage || usage.length === 0 ? (
            <p className="text-sm text-neutral-500">This tag isn&apos;t used anywhere yet.</p>
          ) : (
            <ul className="divide-y divide-neutral-100">
              {usage.map(u => (
                <li key={`${u.resourceType}-${u.resourceId}`} className="py-2.5 flex items-center justify-between">
                  <span className="text-sm text-neutral-800">{u.resourceName}</span>
                  <span className="text-xs text-neutral-400 capitalize">
                    {u.resourceType.replace(/_/g, ' ')}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// Tags section
// =============================================================================

const TagsSection: React.FC = () => {
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editing, setEditing] = useState<Tag | null>(null);
  const [usageTag, setUsageTag] = useState<Tag | null>(null);
  const [deleting, setDeleting] = useState<Tag | null>(null);

  const loadTags = useCallback(async () => {
    try {
      setLoading(true);
      const data = await tagService.list();
      setTags(data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load tags');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTags();
  }, [loadTags]);

  const handleSubmit = async (data: CreateTagRequest, id?: string) => {
    if (id) {
      await tagService.update(id, data);
    } else {
      await tagService.create(data);
    }
    await loadTags();
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await tagService.delete(deleting.id);
      await loadTags();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to delete tag');
    } finally {
      setDeleting(null);
    }
  };

  const filtered = tags.filter(t =>
    !search || t.name.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div>
      <SectionHeader
        title="Tags"
        description="Free-form labels you can attach to resources across the platform"
      />

      {error && (
        <div className="mb-4 p-3 bg-danger-50 border border-danger-200 rounded-lg text-sm text-danger-700 flex items-center justify-between">
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="text-danger-500 hover:text-danger-700 font-medium"
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="mb-6 flex items-center gap-3">
        <SearchBar value={search} onChange={setSearch} placeholder="Search tags..." />
        <div className="ml-auto">
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              setEditing(null);
              setIsFormOpen(true);
            }}
          >
            New Tag
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-neutral-500 py-6">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-neutral-300 border-t-primary-600" />
          <span className="text-sm">Loading tags...</span>
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          title={search ? 'No tags match your search' : 'No tags yet'}
          description={
            search
              ? 'Try a different search.'
              : 'Create a tag to start organising events and other resources.'
          }
          action={
            search
              ? undefined
              : {
                  label: 'New Tag',
                  onClick: () => {
                    setEditing(null);
                    setIsFormOpen(true);
                  },
                }
          }
        />
      ) : (
        <Card variant="default" className="overflow-hidden">
          <table className="w-full">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                  Tag
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-neutral-500 uppercase tracking-wider w-24">
                  Events
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-neutral-500 uppercase tracking-wider w-28">
                  Workflows
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-neutral-500 uppercase tracking-wider w-28">
                  Dashboards
                </th>
                <th className="px-4 py-3 w-40 text-right text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {filtered.map(tag => (
                <tr key={tag.id} className="hover:bg-neutral-50 transition-colors">
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => setUsageTag(tag)}
                      className="cursor-pointer hover:opacity-80 transition-opacity"
                      title="Show where used"
                    >
                      <TagBadge tag={tag} />
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm text-neutral-600 truncate max-w-md">
                    {tag.description || <span className="text-neutral-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right text-sm tabular-nums">
                    <CountCell value={tag.eventCount} onClick={() => setUsageTag(tag)} />
                  </td>
                  <td className="px-4 py-3 text-right text-sm tabular-nums">
                    <CountCell value={tag.workflowCount} onClick={() => setUsageTag(tag)} />
                  </td>
                  <td className="px-4 py-3 text-right text-sm tabular-nums">
                    <CountCell value={tag.dashboardCount} onClick={() => setUsageTag(tag)} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setEditing(tag);
                          setIsFormOpen(true);
                        }}
                        className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded shadow-sm hover:bg-primary-700 transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleting(tag)}
                        className="px-3 py-1.5 text-xs font-medium text-danger-600 border border-danger-300 rounded shadow-sm hover:bg-danger-50 transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <TagFormModal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleSubmit}
        editing={editing}
      />

      {usageTag && <UsageDrawer tag={usageTag} onClose={() => setUsageTag(null)} />}

      {deleting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setDeleting(null)} />
          <div className="relative bg-surface rounded-xl shadow-2xl border border-neutral-200 max-w-md w-full mx-auto z-10 p-6">
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">Delete Tag</h3>
            <p className="text-sm text-neutral-600 mb-1">
              Delete <strong>{deleting.name}</strong>?
            </p>
            {deleting.usageCount > 0 && (
              <p className="text-xs text-warning-700 mb-4">
                This tag is currently used by {deleting.usageCount} resource
                {deleting.usageCount === 1 ? '' : 's'}. The links will be removed but
                the resources themselves will not be deleted.
              </p>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" size="sm" onClick={() => setDeleting(null)}>
                Cancel
              </Button>
              <Button variant="danger" size="sm" onClick={handleDelete}>
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TagsSection;
